import React, { useState } from 'react';
import {
  FileTextOutlined,
  FolderAddOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAccess } from 'infrastructure/hooks/access/useAccess';
import { useClinicEscalation } from 'infrastructure/hooks/control-panel';
import { useCreateCaseInEhr } from 'infrastructure/hooks/control-panel/use-create-case-in-ehr';
import { useGetClinicEscalationValues } from 'components/organisms/patient-control-panel/hooks/useGetClinicEscalationValues';
import ClinicEscalationFormModal from 'components/organisms/controlPanel/clinicalEscalation/modal';
import ClinicalNotesLoggedModal from 'components/molecules/clinical-notes-logged-modal';
import CreateCaseInEhrModalNew from 'components/molecules/create-case-in-ehr';
import { ModuleType } from 'infrastructure/enums';
import useUser from 'utils/useUser';

import s from './styles.module.scss';

import type { FC } from 'react';

interface ClinicalDocButtonsProps {
  patientGuid: string;
  module: ModuleType;
  clinicGuid?: string;
}

export const ClinicalDocButtons: FC<ClinicalDocButtonsProps> = ({
  patientGuid,
  module,
  clinicGuid,
}) => {
  const { t } = useTranslation();

  const [clinicalNotesModule, setClinicalNotesModule] =
    useState<ModuleType | null>(null);

  const [escalationOpen, setEscalationOpen] = useState(false);

  const [ehrOpen, setEhrOpen] = useState(false);

  const { isAdmin, isAgencyDoctor, isPractice } = useUser();

  const {
    isCombinedModule,
    isRpmModule,
    isCcmModule,
    isBlockCcmModule,
    isReadOnlyRole,
    isCcmActionAllowed,
    isCcmActionDisabled,
    isActiveRpmStatus,
    isBlockRpmModule,
  } = useAccess({
    module,
    patientGuid,
  });

  const { defaultClinicEscalationValues, loading: escalationLoading } =
    useGetClinicEscalationValues({ patientGuid });

  const { sendClinicEscalation, sendClinicEscalationLoading } =
    useClinicEscalation({
      onSuccess() {
        setEscalationOpen(false);
      },
    });

  const { createCaseInEhr, createCaseInEhrLoading } = useCreateCaseInEhr({
    onSuccess() {
      setEhrOpen(false);
    },
  });

  const escalationDisabled =
    (isCombinedModule && (isReadOnlyRole || isBlockRpmModule)) ||
    sendClinicEscalationLoading;

  const rpmDisable = !isActiveRpmStatus;
  const ccmDisable =
    (isCombinedModule &&
      (isBlockCcmModule || isReadOnlyRole || !isCcmActionAllowed)) ||
    isCcmActionDisabled;

  const rpmRender = isRpmModule || isCombinedModule;
  const ccmRender = isCcmModule || isCombinedModule;

  return (
    <>
      {(isRpmModule || isCombinedModule) && !isPractice && (
        <>
          {rpmRender && (
            <button
              className={s['doc-button']}
              onClick={() => setClinicalNotesModule(ModuleType.RPM)}
              disabled={rpmDisable}
              data-cy="control-panel-button-rpm-clinical-note"
            >
              <FileTextOutlined className={s['doc-icon']} />
              <span>{t('labels.rpmClinicalNote')}</span>
            </button>
          )}

          {ccmRender && (
            <button
              className={s['doc-button']}
              onClick={() => setClinicalNotesModule(ModuleType.CCM)}
              disabled={ccmDisable}
              data-cy="control-panel-button-ccm-clinical-note"
            >
              <FileTextOutlined className={s['doc-icon']} />
              <span>{t('labels.ccmClinicalNote')}</span>
            </button>
          )}
        </>
      )}

      {(isAdmin || isAgencyDoctor) && (
        <button
          className={s['doc-button']}
          onClick={() => setEhrOpen(true)}
          disabled={
            createCaseInEhrLoading ||
            (isCombinedModule ? isReadOnlyRole : isCcmActionDisabled)
          }
          data-cy="control-panel-button-create-case-ehr"
        >
          <FolderAddOutlined className={s['doc-icon']} />
          <span>{t('labels.createCaseInEHR')}</span>
        </button>
      )}

      {(isRpmModule || isCombinedModule) && !isPractice && (
        <button
          className={s['doc-button']}
          onClick={() => setEscalationOpen(true)}
          disabled={escalationDisabled || escalationLoading}
          data-cy="control-panel-button-clinical-escalations"
        >
          <ExclamationCircleOutlined className={s['doc-icon-red']} />
          <span>{t('labels.clinicalEscalations')}</span>
        </button>
      )}

      <CreateCaseInEhrModalNew
        isOpen={ehrOpen}
        toggle={() => setEhrOpen(false)}
        module={module}
        patientGuid={patientGuid}
        submit={createCaseInEhr}
        loading={createCaseInEhrLoading}
        tabsProps={{
          showTabs: isCombinedModule,
          disableCcm: isBlockCcmModule,
          disableRpm: isBlockRpmModule,
        }}
      />

      {clinicalNotesModule && (
        <ClinicalNotesLoggedModal
          module={clinicalNotesModule}
          patientGuid={patientGuid}
          open
          onClose={() => setClinicalNotesModule(null)}
        />
      )}

      {escalationOpen && (
        <ClinicEscalationFormModal
          isOpen={escalationOpen}
          closeModal={() => setEscalationOpen(false)}
          onSubmit={(values: any) => {
            sendClinicEscalation({
              patientGuid,
              ...values,
            });
          }}
          submitLoading={sendClinicEscalationLoading}
          defaultValue={defaultClinicEscalationValues}
          clinicGuid={clinicGuid}
        />
      )}
    </>
  );
};
